import React, { useState } from 'react';
import { Search, Filter, ShieldCheck, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { RecommendationFilterState } from '@/types/recommendation';
import { RecommendationFilterPopover } from './RecommendationFilterPopover';

interface RecommendationSearchBarProps {
  filter: RecommendationFilterState;
  onFilterChange: (filter: RecommendationFilterState) => void;
  availableRoles: string[];
  availableLocations: string[];
  availableDepartments: string[];
  defaultRoleFilter?: string | null;
  onClearDefaultRole?: () => void;
}

function countActiveFilters(filter: RecommendationFilterState, defaultRole?: string | null): number {
  let count = 0;
  if (filter.roleFilter && filter.roleFilter !== defaultRole) count++;
  if (filter.departmentFilter) count++;
  return count;
}

export function RecommendationSearchBar({
  filter,
  onFilterChange,
  availableRoles,
  availableLocations,
  availableDepartments,
  defaultRoleFilter,
  onClearDefaultRole,
}: RecommendationSearchBarProps) {
  const [filterOpen, setFilterOpen] = useState(false);

  const isUsingDefaultRole =
    !!defaultRoleFilter && filter.roleFilter === defaultRoleFilter;

  const activeCount = countActiveFilters(filter, defaultRoleFilter);

  const filterButton = (
    <div className="relative">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-8 w-8 shrink-0 bg-background"
        aria-label="Open filters"
      >
        <Filter className="h-4 w-4" />
      </Button>
      {activeCount > 0 && (
        <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none pointer-events-none">
          {activeCount}
        </span>
      )}
    </div>
  );

  return (
    <div className="space-y-2">
      {isUsingDefaultRole && (
        <div className="flex items-center gap-1.5 accent-teal">
          <Badge variant="accent" accent="teal" className="gap-1 text-xs pl-1.5 pr-1 py-0.5 h-6">
            <ShieldCheck className="h-3 w-3 shrink-0" />
            <span className="truncate">
              Showing <span className="font-semibold">{defaultRoleFilter}</span> matches
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => {
                    onFilterChange({ ...filter, roleFilter: null });
                    onClearDefaultRole?.();
                  }}
                  className="ml-0.5 h-4 w-4 flex items-center justify-center rounded-full hover:bg-black/10 transition-colors shrink-0"
                  aria-label={`Remove ${defaultRoleFilter} role filter and show all roles`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">Show all roles</TooltipContent>
            </Tooltip>
          </Badge>
        </div>
      )}

      {defaultRoleFilter && !isUsingDefaultRole && filter.roleFilter === null && (
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className="gap-1 text-xs pl-1.5 pr-2 py-0.5 h-6 text-muted-foreground">
            <ShieldCheck className="h-3 w-3 shrink-0" />
            Showing all roles
          </Badge>
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            className="h-8 text-xs pl-7 pr-2"
            placeholder="Search name, role, skill…"
            value={filter.searchQuery}
            onChange={e => onFilterChange({ ...filter, searchQuery: e.target.value })}
          />
        </div>
        <RecommendationFilterPopover
          open={filterOpen}
          onOpenChange={setFilterOpen}
          filter={filter}
          onFilterChange={onFilterChange}
          availableRoles={availableRoles}
          availableLocations={availableLocations}
          availableDepartments={availableDepartments}
          trigger={filterButton}
        />
      </div>
    </div>
  );
}
